import React, { useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Upload, Camera, X, AlertCircle, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const FileUpload = ({ 
  uploadType = 'profile_photo', // 'profile_photo' or 'family_crest'
  onUploadSuccess, 
  currentValue = '',
  disabled = false,
  className = ''
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(currentValue);
  const [description, setDescription] = useState('');
  
  const { toast } = useToast();

  // Define allowed types and size limits based on upload type
  const config = {
    profile_photo: {
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      maxSize: 10 * 1024 * 1024, // 10MB
      accept: 'image/jpeg,image/png,image/gif,image/webp',
      endpoint: '/api/wasabi-uploads/profile-photo'  // Updated to use Wasabi CDN
    },
    family_crest: {
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/tiff'],
      maxSize: 10 * 1024 * 1024, // 10MB
      accept: 'image/jpeg,image/png,image/gif,image/webp,image/svg+xml,image/tiff',
      endpoint: '/api/wasabi-uploads/family-crest'  // Updated to use Wasabi CDN
    }
  };

  const currentConfig = config[uploadType];

  const handleFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    
    if (!file) {
      setSelectedFile(null);
      setPreviewUrl(currentValue);
      return;
    }

    // Validate file type
    if (!currentConfig.allowedTypes.includes(file.type)) {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: `Please select: ${currentConfig.allowedTypes.join(', ')}`
      });
      return;
    }

    // Validate file size
    if (file.size > currentConfig.maxSize) {
      toast({
        variant: "destructive",
        title: "File too large",
        description: `Maximum size is ${currentConfig.maxSize / (1024 * 1024)}MB`
      });
      return;
    }

    setSelectedFile(file);

    // Create preview URL for supported image types
    if (file.type.startsWith('image/') && file.type !== 'image/svg+xml') {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }
  }, [currentConfig, currentValue, toast]);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', selectedFile);
    
    if (uploadType === 'family_crest' && description) {
      formData.append('description', description);
    }

    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_BASE_URL}${currentConfig.endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const result = await response.json();

      // Handle both legacy S3 format and new Wasabi CDN format
      const responseData = result.data || result; // Wasabi returns nested data structure
      
      if (result.success || responseData.url) {
        toast({
          title: "Success!",
          description: result.message || `${uploadType === 'profile_photo' ? 'Profile photo' : 'Family crest'} uploaded successfully`
        });

        // Update preview with uploaded image
        const uploadedUrl = responseData.url || result.url;
        setPreviewUrl(uploadedUrl);
        
        // Call success callback with the data
        if (onUploadSuccess) {
          onUploadSuccess({
            url: uploadedUrl,
            filename: responseData.filename || result.filename,
            original_filename: responseData.original_filename || result.original_filename,
            size: responseData.size || result.size,
            content_type: responseData.content_type || result.content_type
          });
        }

        // Reset form
        setSelectedFile(null);
        setDescription('');
        setUploadProgress(0);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: error.message || 'Please try again'
      });
    } finally {
      setUploading(false);
    }
  }, [selectedFile, description, uploadType, currentConfig, onUploadSuccess, toast]);

  const clearSelection = () => {
    setSelectedFile(null);
    setPreviewUrl(currentValue);
    setDescription('');
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Current Image Preview */}
      {previewUrl && (
        <div className="flex justify-center">
          <img 
            src={previewUrl} 
            alt={uploadType === 'profile_photo' ? 'Profile photo' : 'Family crest'}
            className={`
              ${uploadType === 'profile_photo' 
                ? 'w-32 h-32 rounded-full object-cover' 
                : 'max-w-48 max-h-48 object-contain'
              } 
              border-2 border-gray-200 shadow-sm
            `}
          />
        </div>
      )}

      {/* File Input */}
      <div className="space-y-2">
        <Label htmlFor={`file-${uploadType}`}>
          Choose {uploadType === 'profile_photo' ? 'Profile Photo' : 'Family Crest'}
        </Label>
        <Input
          id={`file-${uploadType}`}
          type="file"
          accept={currentConfig.accept}
          onChange={handleFileSelect}
          disabled={uploading || disabled}
          className="cursor-pointer"
        />
      </div>

      {/* Description Field (for family crest only) */}
      {uploadType === 'family_crest' && (
        <div className="space-y-2">
          <Label htmlFor="description">Description (optional)</Label>
          <Input
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your family crest..."
            disabled={uploading}
            maxLength={500}
          />
        </div>
      )}

      {/* Selected File Info */}
      {selectedFile && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {selectedFile.type.startsWith('image/') ? <Camera className="w-4 h-4 text-blue-600" /> : <Upload className="w-4 h-4 text-blue-600" />}
              <span className="text-sm font-medium text-blue-900">{selectedFile.name}</span>
              <span className="text-xs text-blue-600">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              disabled={uploading}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span>Uploading...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Upload Button */}
      {selectedFile && (
        <Button
          onClick={handleUpload}
          disabled={uploading || disabled}
          className="w-full"
        >
          <Upload className="w-4 h-4 mr-2" />
          {uploading 
            ? 'Uploading...' 
            : `Upload ${uploadType === 'profile_photo' ? 'Profile Photo' : 'Family Crest'}`
          }
        </Button>
      )}

      {/* Help Text */}
      <p className="text-xs text-gray-500">
        {uploadType === 'profile_photo' 
          ? 'Supported formats: JPEG, PNG, GIF, WebP. Maximum size: 5MB'
          : 'Supported formats: JPEG, PNG, GIF, WebP, SVG, TIFF. Maximum size: 10MB'
        }
      </p>
    </div>
  );
};

export default FileUpload;